/**
 * Компонент просмотра логов синхронизации (KlsSyncLogs)
 * 
 * Функциональность:
 * - Отображение списка логов синхронизации
 * - Фильтрация по источнику, статусу, дате
 * - Детальный просмотр лога
 * - Отображение статистики синхронизации
 * - Цветовая индикация статусов
 * - Форматирование длительности и дат
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { klsSyncLogsApi, klsExternalSourcesApi } from "@/services/kls-api.service";
import type { KlsSyncLogs } from "@/types/api-v2";
import { Search, X, Eye, AlertCircle, CheckCircle2, Clock, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ru } from "date-fns/locale";
import { ReferencesSectionHeader } from "@/components/admin/ReferencesSectionHeader";

export default function SyncLogsView() {
  const [selectedLog, setSelectedLog] = useState<KlsSyncLogs | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<number | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");

  // Загрузка источников для фильтра
  const { data: sources = [] } = useQuery({
    queryKey: ["kls-external-sources"],
    queryFn: () => klsExternalSourcesApi.list(),
  });

  // Загрузка логов синхронизации
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["kls-sync-logs", selectedSourceId, selectedStatus],
    queryFn: () =>
      klsSyncLogsApi.list({
        source_id: selectedSourceId,
        status: selectedStatus,
      }),
  });

  // Фильтрация по поисковому запросу
  const filteredLogs = logs.filter((log) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const source = sources.find((s) => s.id === log.source_id);
    return (
      source?.name.toLowerCase().includes(query) ||
      log.error_message?.toLowerCase().includes(query) ||
      log.sync_type.toLowerCase().includes(query)
    );
  });

  // Открытие детального просмотра
  const openDetailDialog = (log: KlsSyncLogs) => {
    setSelectedLog(log);
    setIsDetailDialogOpen(true);
  };

  // Форматирование длительности
  const formatDuration = (seconds?: number) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${seconds} сек`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} мин ${seconds % 60} сек`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} ч ${minutes} мин`;
  };

  // Получение бейджа статуса
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "success":
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Успешно
          </Badge>
        );
      case "error":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Ошибка
          </Badge>
        );
      case "partial":
        return (
          <Badge variant="secondary">
            <AlertCircle className="mr-1 h-3 w-3" />
            Частично
          </Badge>
        );
      case "running":
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3 animate-spin" />
            Выполняется
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Получение источника по ID
  const getSourceName = (sourceId: number) => {
    const source = sources.find((s) => s.id === sourceId);
    return source?.name || `Источник #${sourceId}`;
  };

  return (
    <div className="space-y-4">
      <ReferencesSectionHeader
        title="Логи синхронизации"
        description="Журнал запусков синхронизации с внешних источников: статус, длительность, число обработанных записей и текст ошибки."
      />

      {/* Фильтры */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по источнику или ошибке..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              onClick={() => setSearchQuery("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Select
          value={selectedSourceId?.toString() || "all"}
          onValueChange={(value) =>
            setSelectedSourceId(value === "all" ? undefined : parseInt(value))
          }
        >
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="Все источники" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все источники</SelectItem>
            {sources.map((source) => (
              <SelectItem key={source.id} value={source.id.toString()}>
                {source.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={selectedStatus || "all"}
          onValueChange={(value) => setSelectedStatus(value === "all" ? undefined : value)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="success">Успешно</SelectItem>
            <SelectItem value="error">Ошибка</SelectItem>
            <SelectItem value="partial">Частично</SelectItem>
            <SelectItem value="running">Выполняется</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Таблица */}
      <div className="border rounded-md overflow-hidden">
        <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Источник</TableHead>
                <TableHead>Тип синхронизации</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Время начала</TableHead>
                <TableHead>Длительность</TableHead>
                <TableHead>Обработано</TableHead>
                <TableHead>Создано/Обновлено</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-6">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-6">
                    {searchQuery || selectedSourceId || selectedStatus
                      ? "Ничего не найдено"
                      : "Логи синхронизации отсутствуют"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{log.id}</TableCell>
                    <TableCell className="font-medium">{getSourceName(log.source_id)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{log.sync_type}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(log.status)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{new Date(log.started_at).toLocaleString("ru-RU")}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(log.started_at), {
                            addSuffix: true,
                            locale: ru,
                          })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.duration_seconds
                        ? formatDuration(log.duration_seconds)
                        : log.status === "running"
                        ? "Выполняется..."
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {log.records_processed !== undefined ? log.records_processed : "-"}
                    </TableCell>
                    <TableCell>
                      {log.records_created !== undefined || log.records_updated !== undefined ? (
                        <div className="flex flex-col text-sm">
                          <span className="text-green-600">
                            +{log.records_created || 0} создано
                          </span>
                          <span className="text-blue-600">
                            ~{log.records_updated || 0} обновлено
                          </span>
                          {log.records_deleted !== undefined && log.records_deleted > 0 && (
                            <span className="text-red-600">
                              -{log.records_deleted} удалено
                            </span>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDetailDialog(log)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Диалог детального просмотра */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Детали синхронизации #{selectedLog?.id}</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Источник</Label>
                  <p className="text-sm font-medium">{getSourceName(selectedLog.source_id)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Статус</Label>
                  <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    Тип синхронизации
                  </Label>
                  <p className="text-sm">{selectedLog.sync_type}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Режим</Label>
                  <p className="text-sm">{selectedLog.sync_mode}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Время начала</Label>
                  <p className="text-sm">
                    {new Date(selectedLog.started_at).toLocaleString("ru-RU")}
                  </p>
                </div>
                {selectedLog.completed_at && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">
                      Время завершения
                    </Label>
                    <p className="text-sm">
                      {new Date(selectedLog.completed_at).toLocaleString("ru-RU")}
                    </p>
                  </div>
                )}
                {selectedLog.duration_seconds && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Длительность</Label>
                    <p className="text-sm">{formatDuration(selectedLog.duration_seconds)}</p>
                  </div>
                )}
              </div>

              {/* Статистика */}
              {(selectedLog.records_processed !== undefined ||
                selectedLog.records_created !== undefined ||
                selectedLog.records_updated !== undefined) && (
                <div className="border rounded-md p-4">
                  <Label className="text-sm font-medium mb-2 block">Статистика обработки</Label>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Обработано:</span>{" "}
                      <span className="font-medium">{selectedLog.records_processed || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Создано:</span>{" "}
                      <span className="font-medium text-green-600">
                        {selectedLog.records_created || 0}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Обновлено:</span>{" "}
                      <span className="font-medium text-blue-600">
                        {selectedLog.records_updated || 0}
                      </span>
                    </div>
                    {selectedLog.records_deleted !== undefined && (
                      <div>
                        <span className="text-muted-foreground">Удалено:</span>{" "}
                        <span className="font-medium text-red-600">
                          {selectedLog.records_deleted || 0}
                        </span>
                      </div>
                    )}
                    {selectedLog.records_skipped !== undefined && (
                      <div>
                        <span className="text-muted-foreground">Пропущено:</span>{" "}
                        <span className="font-medium text-yellow-600">
                          {selectedLog.records_skipped || 0}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ошибка */}
              {selectedLog.error_message && (
                <div className="border rounded-md p-4 border-destructive">
                  <Label className="text-sm font-medium text-destructive mb-2 block">
                    Ошибка
                  </Label>
                  <p className="text-sm text-destructive">{selectedLog.error_message}</p>
                  {selectedLog.error_details && (
                    <details className="mt-2">
                      <summary className="text-sm cursor-pointer text-muted-foreground">
                        Детали ошибки
                      </summary>
                      <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                        {JSON.stringify(selectedLog.error_details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Конфигурация */}
              {selectedLog.sync_config && (
                <div className="border rounded-md p-4">
                  <Label className="text-sm font-medium mb-2 block">Конфигурация синхронизации</Label>
                  <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                    {JSON.stringify(selectedLog.sync_config, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
